<template>
  <tbody
    :class="{
      'is-selecting-range': selecting,
      'is-selected-range': true,
    }"
  >
    <tr v-for="(row, idx) in rows" :key="idx" :style="rowStyle(row, idx + 1)">
      <grid-cell
        v-for="(column, columnIndex) in columns"
        :key="columnIndex"
        :row="row"
        :column="column"
        :name="`cell_${columnIndex + 1}`"
        :editing-position="editingPosition"
        :selected-range-method="selectedRangeMethod"
        :class="cellClass(row, column)"
        @double-click="handleDoubleClick"
        @click="handleClick"
        @mouse-down="handleMouseDown"
        @mouse-enter="handleMouseEnter"
        @mouse-up="handleMouseUp"
        @change-value="handleChangeValue"
        ><template v-slot="{ cell, editing }"
          ><slot :cell="cell" :editing="editing" /></template
      ></grid-cell>
    </tr>
  </tbody>
</template>
<script>
import GridCell from "@/components/grid/Cell";
import { CellPosition } from "@/components/excel/schema/BaseSchema.ts";

export default {
  name: "grid-body-content",
  components: { GridCell },
  props: {
    rows: {
      type: Array,
    },
    columns: {
      type: Array,
    },
    classMethod: {
      type: Function,
    },
    selectedRangeMethod: Function,
    lineHeightMethod: Function,
    readonly: Boolean
  },
  data() {
    return {
      selecting: false,
      selected: {
        start: new CellPosition(1, 1),
        end: new CellPosition(1, 1),
      },
      editingPosition: {},
    };
  },
  computed: {
    isSelectedRange() {
      const { start, end } = this.selected;
      return start && end;
    },
    selectedRange() {
      const { start, end } = this.selected;
      if (start && end) {
        const left = Math.min(start.columnIndex, end.columnIndex);
        const right = Math.max(start.columnIndex, end.columnIndex);
        const top = Math.min(start.rowIndex, end.rowIndex);
        const bottom = Math.max(start.rowIndex, end.rowIndex);
        return { left, right, top, bottom };
      }
      return {};
    },
  },
  watch: {
    selectedRange(value) {
      const params = { range: value, ...this.selected };
      this.emitSelectedRange(params);
    },
  },
  methods: {
    handleDoubleClick(params, event) {
      //双击单元格 需要清理选中的区域
      this.cancelSelectedRange();
      if (!params.cell.editor) {
        // 双击的单元格没有配置编辑器 直接返回
        return;
      }
      if (this.readonly){
        return;
      }
      const { rowIndex, columnIndex } = params;
      this.editingPosition = { rowIndex, columnIndex };
      this.$emit("double-click-cell", params, event);
    },
    handleClick(params, event) {
      // 如果鼠标弹起和鼠标按下是同一个元素触发的 将会在弹起事件之后触发鼠标单击事件
      this.selectCell(params);
      // 如果单击的单元格不是当前正在编辑的单元格 结束编辑状态
      this.cancelEditing();
      console.log("触发了单元格点击事件");
      if (this.$listeners["click-cell"]) {
        this.$emit("click-cell", params, event);
      }
    },
    handleMouseDown(params, event) {
      this.cancelEditing();
      this.selecting = true;
      const { rowIndex, columnIndex } = params;
      const position = new CellPosition(rowIndex, columnIndex);
      this.emitSelectedRange({ start: position, end: position }, event);
      // this.selected = { start: position, end: position };
      this.$emit("mouse-down-cell", params, event);
    },
    handleMouseEnter(params, event) {
      this.$emit("mouse-enter-cell", params, event);
      if (!this.selecting) {
        return;
      }
      const { rowIndex, columnIndex } = params;
      const position = new CellPosition(rowIndex, columnIndex);
      const { start, end } = this.selected;
      // console.log(this.selected, this.selecting);
      if (position.isSameCell(end)) {
        return;
      }
      // 需要检查首尾两点的横纵坐标所在的轴线上没有被合并的行 否则需要移动首尾坐标点
      if (position.isBefore(start)) {
        const trajectory = this.checkRange(position, start);
        const endPoint = trajectory.start;
        const startPoint = trajectory.end;
        // this.selected = { start: startPoint, end: endPoint };
        this.emitSelectedRange({ start: startPoint, end: endPoint }, event);
      } else {
        const trajectory = this.checkRange(start, position);
        const endPoint = trajectory.end;
        const startPoint = trajectory.start;
        // this.selected = { start: startPoint, end: endPoint };
        this.emitSelectedRange({ start: startPoint, end: endPoint }, event);
      }
      this.$emit("change-selected-cells", params, event);
    },
    handleMouseUp(params, event) {
      if (!this.selecting) {
        return;
      }
      this.selecting = false;
      const { start, end } = this.selected;
      if (end.isBefore(start)) {
        this.selected = { start: end, end: start };
      }
      this.$emit("mouse-up-cell", params, event);
    },
    cancelSelectedRange() {
      const { start, end } = this.selected;
      if (start || end) {
        this.selected = {};
      }
    },
    emitSelectedRange(params, event) {
      if (this.$listeners["selected-range"]) {
        console.log(params);
        this.$emit("selected-range", params, event);
      }
    },
    cancelEditing() {
      const { rowIndex, columnIndex } = this.editingPosition;
      if (rowIndex || columnIndex) {
        this.editingPosition = {};
      }
    },
    selectCell({ rowIndex, columnIndex }) {
      const { start, end } = this.selected;
      const position = new CellPosition(rowIndex, columnIndex);
      if (!start || !start.isSameCell(position)) {
        this.selected.start = position;
      }
      if (!end || !end.isSameCell(position)) {
        this.selected.end = position;
      }
    },
    /**
     * 暴露给外部的接口
     * @param start
     * @param end
     */
    selectRange({ start, end }) {
      const startPosition = new CellPosition(start.rowIndex, start.columnIndex);
      const endPosition = new CellPosition(end.rowIndex, end.columnIndex);
      this.selected = { start: startPosition, end: endPosition };
    },
    checkRange(start, end) {
      const startNeedMoveUp = this.rows.some((row) => {
        if (row.index < start.rowIndex || row.index > end.rowIndex) {
          return false;
        }
        return row.cells.some((cell) => {
          return cell.rowSpan && cell.rowSpan < 1;
        });
      });
      if (startNeedMoveUp) {
        const nextStart = {
          rowIndex: start.rowIndex - 1,
          columnIndex: start.columnIndex,
        };
        return this.checkRange(nextStart, end);
      }
      const endNeedMoveDown = this.rows.some((row) => {
        if (row.index < start.rowIndex || row.index > end.rowIndex) {
          return false;
        }
        return row.cells.some((cell) => {
          return cell.rowSpan && cell.rowSpan < 1;
        });
      });
      if (endNeedMoveDown) {
        const nextEnd = {
          rowIndex: end.rowIndex + 1,
          columnIndex: start.columnIndex,
        };
        return this.checkRange(start, nextEnd);
      }
      const startColumn = this.columns.find(
        (it) => it.index === start.columnIndex
      );
      const startNeedMoveLeft = this.rows.some((row) => {
        if (row.index < start.rowIndex || row.index > end.rowIndex) {
          return false;
        }
        const cell = row.cells[startColumn.property];
        return cell.span && cell.span.colSpan < 1;
      });
      if (startNeedMoveLeft) {
        const nextStart = {
          rowIndex: start.rowIndex,
          columnIndex: start.columnIndex - 1,
        };
        return this.checkRange(nextStart, end);
      }
      const endColumn = this.columns.find((it) => it.index === end.columnIndex);
      const endNeedMoveRight = this.rows.some((row) => {
        if (row.index < start.rowIndex || row.index > end.rowIndex) {
          return false;
        }
        const cell = row.cells[endColumn.property];
        return cell.span && cell.span.colSpan < 1;
      });
      if (endNeedMoveRight) {
        const nextEnd = {
          rowIndex: end.rowIndex,
          columnIndex: end.columnIndex + 1,
        };
        return this.checkRange(start, nextEnd);
      }
      return { start, end };
    },
    cellClass(row, column) {
      const { classMethod, selectedRange } = this;
      if (classMethod) {
        return classMethod({ row, column, selectedRange });
      }
      return {};
    },
    handleChangeValue(payload) {
      this.$emit("change-value", payload);
    },
    rowStyle(row, index){
      const { lineHeightMethod } = this.$props;
      if (lineHeightMethod){
        const lineHeight = lineHeightMethod({ row: { index }});
        if (lineHeight){
          return { lineHeight };
        }
      }
      return {};
    }
  },
};
</script>
